import { useCallback, useEffect, useRef, useState } from "react";
import { disableWhatsApp, fetchWhatsAppStatus, startWhatsAppActivation, type WhatsAppStatus } from "@/services/whatsapp";

/**
 * Estado de los avisos por WhatsApp del usuario. `activate()` abre WhatsApp con
 * el código y, mientras la persona escribe, consulta cada pocos segundos hasta
 * que n8n confirma la activación.
 */
export function useWhatsApp() {
  const [status, setStatus] = useState<WhatsAppStatus | null>(null);
  const [waiting, setWaiting] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const pollRef = useRef<number | null>(null);

  const stopPolling = useCallback(() => {
    if (pollRef.current !== null) {
      window.clearInterval(pollRef.current);
      pollRef.current = null;
    }
    setWaiting(false);
  }, []);

  const refresh = useCallback(async () => {
    try {
      const s = await fetchWhatsAppStatus();
      setStatus(s);
      return s;
    } catch {
      return null;
    }
  }, []);

  useEffect(() => {
    refresh();
    return () => {
      if (pollRef.current !== null) window.clearInterval(pollRef.current);
    };
  }, [refresh]);

  const activate = useCallback(async () => {
    setError(null);
    // Se abre la ventana de inmediato (con el clic) para que el navegador no la bloquee.
    const win = window.open("", "_blank");
    try {
      const res = await startWhatsAppActivation();
      if (!res) {
        win?.close();
        setError("Los avisos por WhatsApp todavía no están habilitados.");
        return;
      }
      if (win) win.location.href = res.url;
      else window.location.href = res.url;
      setWaiting(true);
      let tries = 0;
      pollRef.current = window.setInterval(async () => {
        tries += 1;
        const s = await refresh();
        if (s?.optedIn || tries >= 60) stopPolling();
      }, 4000);
    } catch {
      win?.close();
      setError("No se pudo iniciar la activación. Intenta de nuevo.");
    }
  }, [refresh, stopPolling]);

  const disable = useCallback(async () => {
    setError(null);
    try {
      await disableWhatsApp();
      await refresh();
    } catch {
      setError("No se pudo desactivar. Intenta de nuevo.");
    }
  }, [refresh]);

  return { status, waiting, error, activate, disable, refresh };
}
